import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Wand2, Upload, Plus, Trash2, Save, ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import CardPreview from "@/components/CardPreview";

const EMPTY = {
  full_name: "", credentials: "", job_title: "", tagline: "", company: "", department: "", bio: "", email: "", phone: "", fax: "", website: "", location: "",
  avatar_url: "", logo_url: "", brand_color: "#D4AF37", logo_fit: "cover",
  socials: { linkedin: "", twitter: "", instagram: "", facebook: "", youtube: "", whatsapp: "" },
  products: [], gallery: [], client_account_id: null,
};

const Field = ({ label, ...props }) => (
  <div>
    <label className="text-xs text-[#8B9BB4] font-semibold mb-1.5 block">{label}</label>
    <input className="iw-input" {...props} />
  </div>
);

export default function CardBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [aiUrl, setAiUrl] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);
  const [taglines, setTaglines] = useState([]);
  const [tagLoading, setTagLoading] = useState(false);
  const avatarRef = useRef();
  const logoRef = useRef();
  const galleryRef = useRef();

  useEffect(() => {
    if (id) api.get(`/cards/${id}`).then((r) => setCard({ ...EMPTY, ...r.data, socials: { ...EMPTY.socials, ...r.data.socials } })).catch(() => toast.error("Card not found"));
  }, [id]);

  const set = (k, v) => setCard((c) => ({ ...c, [k]: v }));
  const setSocial = (k, v) => setCard((c) => ({ ...c, socials: { ...c.socials, [k]: v } }));

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const onAvatar = async (e) => { const f = e.target.files[0]; if (!f) return; toast.promise(upload(f).then((u) => set("avatar_url", u)), { loading: "Uploading…", success: "Photo added", error: "Upload failed" }); };
  const onLogo = async (e) => { const f = e.target.files[0]; if (!f) return; toast.promise(upload(f).then((u) => set("logo_url", u)), { loading: "Uploading…", success: "Logo added", error: "Upload failed" }); };
  const onGallery = async (e) => { const files = [...e.target.files]; if (!files.length) return; toast.promise(Promise.all(files.map(upload)).then((urls) => setCard((c) => ({ ...c, gallery: [...c.gallery, ...urls].slice(0, 10) }))), { loading: "Uploading…", success: "Added to portfolio", error: "Upload failed" }); };

  const runAutofill = async () => {
    if (!aiUrl.trim()) { toast.error("Enter a website or LinkedIn URL"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/autofill", { url: aiUrl });
      setCard((c) => ({
        ...c,
        full_name: data.full_name || c.full_name, job_title: data.job_title || c.job_title,
        company: data.company || c.company, bio: data.bio || c.bio, email: data.email || c.email,
        phone: data.phone || c.phone, website: data.website || c.website || aiUrl, location: data.location || c.location,
        socials: { ...c.socials, ...(data.socials || {}) },
      }));
      toast.success("Card auto-filled with AI");
    } catch (e) { toast.error(e.response?.data?.detail || "AI could not analyze that source"); }
    finally { setAiLoading(false); }
  };

  const writeBio = async () => {
    setBioLoading(true);
    try {
      const ctx = `${card.full_name}, ${card.job_title} at ${card.company}. ${card.bio}`;
      const { data } = await api.post("/ai/copywrite", { kind: "bio", context: ctx });
      set("bio", data.text);
      toast.success("Bio generated");
    } catch { toast.error("Could not generate bio"); }
    finally { setBioLoading(false); }
  };

  const suggestTaglines = async () => {
    setTagLoading(true);
    try {
      const ctx = `${card.full_name || ""}, ${card.job_title || ""} at ${card.company || ""}. ${card.bio || ""}`.trim();
      const { data } = await api.post("/ai/copywrite", { kind: "tagline", context: ctx });
      const lines = (data.text || "").split("\n")
        .map((l) => l.replace(/^\s*[-•\d.\)]+\s*/, "").replace(/^["'“]|["'”]$/g, "").trim())
        .filter(Boolean).slice(0, 3);
      if (!lines.length) { toast.error("No taglines returned"); return; }
      setTaglines(lines);
    } catch { toast.error("Could not generate taglines"); }
    finally { setTagLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...card, products: (card.products || []).map(({ _key, ...rest }) => rest) };
      if (id) await api.put(`/cards/${id}`, payload);
      else await api.post("/cards", payload);
      toast.success("Card saved");
      navigate("/app/cards");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout title={id ? "Edit Card" : "Card Builder"}
      actions={<div className="flex gap-2">
        <button onClick={() => navigate("/app/cards")} className="btn-ghost px-4 py-2.5 rounded-full text-sm flex items-center gap-2"><ArrowLeft size={16} /> Back</button>
        <button onClick={save} disabled={saving} className="btn-gold px-4 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="save-card-btn">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save</button>
      </div>}>
      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        <div className="space-y-6 fade-up">
          {/* AI Autofill */}
          <div className="iw-card p-6 border-[#D4AF37]/40">
            <p className="overline flex items-center gap-2"><Sparkles size={14} /> AI Auto-Fill</p>
            <p className="text-sm text-[#8B9BB4] mt-1 mb-3">Paste a website or LinkedIn URL — Gemini builds the card for you.</p>
            <div className="flex gap-2">
              <input className="iw-input" placeholder="https://company.com" value={aiUrl} onChange={(e) => setAiUrl(e.target.value)} data-testid="ai-url-input" />
              <button onClick={runAutofill} disabled={aiLoading} className="btn-gold px-5 rounded-lg text-sm flex items-center gap-2 shrink-0" data-testid="ai-autofill-btn">{aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} Fill</button>
            </div>
          </div>

          {/* Images */}
          <div className="iw-card p-6">
            <p className="overline mb-4">Photo & Logo</p>
            <div className="flex gap-4">
              <div>
                <div className="h-20 w-20 rounded-xl bg-[#142B5A] overflow-hidden flex items-center justify-center mb-2">{card.avatar_url ? <img src={fileUrl(card.avatar_url)} alt="" className="h-full w-full object-cover" /> : <Upload size={20} className="text-[#8B9BB4]" />}</div>
                <button onClick={() => avatarRef.current.click()} className="text-xs text-[#D4AF37]" data-testid="upload-avatar-btn">Upload headshot</button>
                <input ref={avatarRef} type="file" accept="image/*" hidden onChange={onAvatar} />
              </div>
              <div>
                <div className="h-20 w-20 rounded-xl bg-[#142B5A] overflow-hidden flex items-center justify-center mb-2">{card.logo_url ? <img src={fileUrl(card.logo_url)} alt="" className="h-full w-full object-contain p-2" /> : <Upload size={20} className="text-[#8B9BB4]" />}</div>
                <button onClick={() => logoRef.current.click()} className="text-xs text-[#D4AF37]" data-testid="upload-logo-btn">Upload logo</button>
                <input ref={logoRef} type="file" accept="image/*" hidden onChange={onLogo} />
                {card.logo_url && (
                  <div className="mt-2 flex rounded-lg border border-[#ffffff1a] overflow-hidden w-fit" data-testid="logo-fit-toggle">
                    {["cover", "contain"].map((f) => (
                      <button key={f} onClick={() => set("logo_fit", f)} data-testid={`logo-fit-${f}`}
                        className={`text-xs px-2.5 py-1 transition-colors ${card.logo_fit === f ? "bg-[#D4AF37] text-[#081426] font-semibold" : "text-[#8B9BB4] hover:text-[#F6F1E4]"}`}>
                        {f === "cover" ? "Fill" : "Fit"}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <label className="text-xs text-[#8B9BB4] font-semibold mb-1.5 block">Brand color</label>
                <input type="color" value={card.brand_color} onChange={(e) => set("brand_color", e.target.value)} className="h-10 w-full rounded-lg bg-transparent cursor-pointer" data-testid="brand-color" />
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="iw-card p-6 space-y-4">
            <p className="overline">Profile Details</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" value={card.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="field-full_name" />
              <Field label="Qualifications & Certifications" value={card.credentials} onChange={(e) => set("credentials", e.target.value)} placeholder="e.g. MBA, PMP, CPA" data-testid="field-credentials" />
              <Field label="Job title" value={card.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="field-job_title" />
              <Field label="Company" value={card.company} onChange={(e) => set("company", e.target.value)} data-testid="field-company" />
              <Field label="Department" value={card.department} onChange={(e) => set("department", e.target.value)} data-testid="field-department" />
              <Field label="Location" value={card.location} onChange={(e) => set("location", e.target.value)} data-testid="field-location" />
              <Field label="Email" type="email" value={card.email} onChange={(e) => set("email", e.target.value)} data-testid="field-email" />
              <Field label="Phone" value={card.phone} onChange={(e) => set("phone", e.target.value)} data-testid="field-phone" />
              <Field label="Fax" value={card.fax} onChange={(e) => set("fax", e.target.value)} data-testid="field-fax" />
              <Field label="Website" value={card.website} onChange={(e) => set("website", e.target.value)} data-testid="field-website" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-[#8B9BB4] font-semibold">Bio</label>
                <button onClick={writeBio} disabled={bioLoading} className="text-xs text-[#D4AF37] flex items-center gap-1" data-testid="ai-bio-btn">{bioLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI write</button>
              </div>
              <textarea className="iw-input" rows={3} value={card.bio} onChange={(e) => set("bio", e.target.value)} data-testid="field-bio" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-[#8B9BB4] font-semibold">Tagline</label>
                <button onClick={suggestTaglines} disabled={tagLoading} className="text-xs text-[#D4AF37] flex items-center gap-1" data-testid="ai-tagline-btn">{tagLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Suggest 3 taglines</button>
              </div>
              <input className="iw-input" placeholder="A short punchy line under your title" value={card.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="field-tagline" />
              {taglines.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="tagline-options">
                  {taglines.map((t, i) => (
                    <button key={t} onClick={() => { set("tagline", t); toast.success("Tagline applied"); }}
                      className="w-full text-left text-sm px-3 py-2.5 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 hover:bg-[#D4AF37]/15 hover:border-[#D4AF37]/60 transition-colors flex items-center justify-between gap-2"
                      data-testid={`tagline-option-${i}`}>
                      <span className="text-[#F6F1E4]">“{t}”</span>
                      <span className="text-xs text-[#D4AF37] shrink-0">Use →</span>
                    </button>
                  ))}
                  <button onClick={suggestTaglines} disabled={tagLoading} className="w-full text-center text-xs text-[#8B9BB4] hover:text-[#D4AF37] transition-colors py-1.5 flex items-center justify-center gap-1.5" data-testid="tagline-regenerate-btn">
                    {tagLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Give me 3 more
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Socials */}
          <div className="iw-card p-6">
            <p className="overline mb-4">Social Links</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {["linkedin", "twitter", "instagram", "facebook", "youtube", "whatsapp"].map((s) => (
                <Field key={s} label={s[0].toUpperCase() + s.slice(1)} value={card.socials[s]} onChange={(e) => setSocial(s, e.target.value)} placeholder={`https://…`} data-testid={`social-${s}`} />
              ))}
            </div>
          </div>

          {/* Products */}
          <div className="iw-card p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="overline">Products & Services</p>
              <button onClick={() => setCard((c) => ({ ...c, products: [...c.products, { _key: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`), title: "", description: "", cta_label: "", cta_url: "" }] }))} className="text-xs text-[#D4AF37] flex items-center gap-1" data-testid="add-product-btn"><Plus size={14} /> Add</button>
            </div>
            <div className="space-y-4">
              {card.products.map((p, i) => (
                <div key={p._key || i} className="border border-[#ffffff1a] rounded-lg p-4 space-y-3">
                  <div className="flex gap-2">
                    <input className="iw-input" placeholder="Title" value={p.title} onChange={(e) => { const pr = [...card.products]; pr[i].title = e.target.value; set("products", pr); }} />
                    <button onClick={() => set("products", card.products.filter((_, x) => x !== i))} className="text-red-400 px-2"><Trash2 size={16} /></button>
                  </div>
                  <input className="iw-input" placeholder="Description" value={p.description} onChange={(e) => { const pr = [...card.products]; pr[i].description = e.target.value; set("products", pr); }} />
                  <div className="grid grid-cols-2 gap-2">
                    <input className="iw-input" placeholder="CTA label" value={p.cta_label} onChange={(e) => { const pr = [...card.products]; pr[i].cta_label = e.target.value; set("products", pr); }} />
                    <input className="iw-input" placeholder="CTA url" value={p.cta_url} onChange={(e) => { const pr = [...card.products]; pr[i].cta_url = e.target.value; set("products", pr); }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Portfolio */}
          <div className="iw-card p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="overline">Portfolio Gallery</p>
              <button onClick={() => galleryRef.current.click()} className="text-xs text-[#D4AF37] flex items-center gap-1" data-testid="add-gallery-btn"><Upload size={14} /> Upload</button>
              <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={onGallery} />
            </div>
            <div className="grid grid-cols-4 gap-2">
              {card.gallery.map((g, i) => (
                <div key={g} className="relative group">
                  <img src={fileUrl(g)} alt="" className="aspect-square object-cover rounded-lg" />
                  <button onClick={() => set("gallery", card.gallery.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-black/70 rounded p-1 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-0 h-fit">
          <p className="overline mb-4">Live Preview</p>
          <CardPreview card={card} />
        </div>
      </div>
    </DashboardLayout>
  );
}
